import {
  MODEL_LABELS,
  calculateAnalyticalDesign,
  calculatePitchMismatch,
  calculateSourceGeometry,
  clamp,
  estimateThickParaxial,
  isFiniteNumber,
  isPositive,
  predictedImageDistance,
  refractVector,
  targetCenters,
  validateThickGeometry,
} from './physics'
import type {
  AppState,
  DistributionKind,
  IntensitySeries,
  MonteCarloResult,
  MonteCarloSettings,
  Percentiles,
  RayPath,
  SimulationResult,
  SimulationSettings,
} from './types'

export interface SimulationCallbacks {
  onProgress?: (progress: number, message: string) => void
  shouldCancel?: () => boolean
}

export class SimulationCancelledError extends Error {
  constructor() {
    super('Simulation cancelled')
    this.name = 'SimulationCancelledError'
  }
}

interface Vec2 {
  x: number
  y: number
}

interface RayAccumulator {
  bins: number[][]
  xMm: number[]
  xMin: number
  xMax: number
  binWidth: number
  launchedByView: number[]
  totalAcceptedByView: number[]
  rays: RayPath[]
  discarded: {
    total: number
    tir: number
    unreachable: number
    missedSurface: number
  }
  exitAngles: Array<{ angle: number; weight: number }>
}

export type NominalPhysicalInputErrors = Record<string, string>

const nextFrame = () => new Promise<void>((resolve) => setTimeout(resolve, 0))

async function checkpoint(
  callbacks: SimulationCallbacks,
  progress: number,
  message: string,
): Promise<void> {
  if (callbacks.shouldCancel?.()) throw new SimulationCancelledError()
  if (callbacks.onProgress) {
    callbacks.onProgress(clamp(progress, 0, 1), message)
    await nextFrame()
    if (callbacks.shouldCancel?.()) throw new SimulationCancelledError()
  }
}

function midpointSamples(width: number, count: number): number[] {
  if (count <= 1 || width === 0) return [0]
  return Array.from({ length: count }, (_, index) =>
    -width / 2 + ((index + 0.5) / count) * width,
  )
}

function repeatIndices(activeWidthMm: number, repeatWidthMm: number, count: number): number[] {
  const halfMaximum = Math.max(2, Math.floor(activeWidthMm / (2 * repeatWidthMm)))
  const desired = Math.max(5, Math.min(count, halfMaximum * 2 + 1))
  const indices = new Set<number>([0, -1, 1, -2, 2])
  const remainingPairs = Math.max(0, Math.floor((desired - indices.size) / 2))
  for (let index = 1; index <= remainingPairs; index += 1) {
    const m = Math.max(3, Math.round((index / remainingPairs) * halfMaximum))
    indices.add(-m)
    indices.add(m)
  }
  return [...indices]
    .filter((value) => Math.abs(value) <= halfMaximum)
    .sort((a, b) => a - b)
    .slice(0, desired)
}

function emissionWeight(state: AppState, angleRad: number): number {
  const magnitude = Math.abs(angleRad)
  if (magnitude >= Math.PI / 2) return 0
  if (state.lcd.emissionModel === 'flat') return 1
  if (state.lcd.emissionModel === 'lambertian') return Math.max(0, Math.cos(magnitude))
  if (!isPositive(state.lcd.gaussianHalfAngleDeg)) return 0
  const halfAngle = (state.lcd.gaussianHalfAngleDeg * Math.PI) / 180
  return Math.exp(-Math.log(2) * (magnitude / halfAngle) ** 2)
}

function createAccumulator(state: AppState, settings: SimulationSettings): RayAccumulator {
  const views = state.view.views
  const span = Math.max(10, settings.viewerSpanMm, state.view.headBoxWidthMm ?? 0)
  const binCount = Math.max(80, Math.round(settings.viewerBins))
  const binWidth = span / binCount
  const xMin = -span / 2
  return {
    bins: Array.from({ length: views }, () => Array(binCount).fill(0)),
    xMm: Array.from({ length: binCount }, (_, index) => xMin + (index + 0.5) * binWidth),
    xMin,
    xMax: span / 2,
    binWidth,
    launchedByView: Array(views).fill(0),
    totalAcceptedByView: Array(views).fill(0),
    rays: [],
    discarded: { total: 0, tir: 0, unreachable: 0, missedSurface: 0 },
    exitAngles: [],
  }
}

function effectiveSimulationSettings(
  state: AppState,
  settings: SimulationSettings,
): SimulationSettings {
  return {
    ...settings,
    viewerSpanMm: Math.max(10, settings.viewerSpanMm, state.view.headBoxWidthMm ?? 0),
  }
}

export function paraxialSourceAngle(reducedAngle: number, sourceIndex: number): number {
  return reducedAngle / sourceIndex
}

function addToBin(accumulator: RayAccumulator, view: number, x: number, weight: number): void {
  accumulator.totalAcceptedByView[view] += weight
  const index = Math.floor((x - accumulator.xMin) / accumulator.binWidth)
  if (index >= 0 && index < accumulator.bins[view].length) {
    accumulator.bins[view][index] += weight
  }
}

function weightedCentroid(x: number[], intensity: number[]): number | null {
  const total = intensity.reduce((sum, value) => sum + value, 0)
  if (total <= 1e-15) return null
  return intensity.reduce((sum, value, index) => sum + value * x[index], 0) / total
}

function characterizeSeries(view: number, xMm: number[], intensity: number[]): IntensitySeries {
  let peakIndex = -1
  let peakValue = 0
  intensity.forEach((value, index) => {
    if (value > peakValue) {
      peakValue = value
      peakIndex = index
    }
  })
  if (peakIndex < 0 || peakValue <= 1e-15) {
    return { view, xMm, intensity, peakMm: null, centroidMm: null, fwhmMm: null }
  }
  const half = peakValue / 2
  let left = peakIndex
  let right = peakIndex
  while (left > 0 && intensity[left - 1] >= half) left -= 1
  while (right < intensity.length - 1 && intensity[right + 1] >= half) right += 1
  const fwhmMm = Math.max(xMm[1] - xMm[0], xMm[right] - xMm[left] + (xMm[1] - xMm[0]))
  const localX = xMm.slice(left, right + 1)
  const localIntensity = intensity.slice(left, right + 1)
  return {
    view,
    xMm,
    intensity,
    peakMm: xMm[peakIndex],
    centroidMm: weightedCentroid(xMm, intensity),
    fwhmMm,
  }
}

function integratePupil(
  bins: number[],
  xMm: number[],
  binWidth: number,
  centerMm: number,
  diameterMm: number,
): number {
  const low = centerMm - diameterMm / 2
  const high = centerMm + diameterMm / 2
  let total = 0
  for (let index = 0; index < bins.length; index += 1) {
    const binLow = xMm[index] - binWidth / 2
    const binHigh = xMm[index] + binWidth / 2
    const overlap = Math.max(0, Math.min(high, binHigh) - Math.max(low, binLow))
    if (overlap > 0) total += bins[index] * (overlap / binWidth)
  }
  return total
}

function assignSourcesToTargets(series: IntensitySeries[], targets: number[]): number[] {
  const valid = series
    .map((item, view) => ({ view, center: item.centroidMm ?? item.peakMm }))
    .filter((item): item is { view: number; center: number } => isFiniteNumber(item.center))
    .sort((a, b) => a.center - b.center)
  if (valid.length !== targets.length) {
    return targets.map((_, targetIndex) => targets.length - 1 - targetIndex)
  }
  const sortedTargetIndices = targets
    .map((center, index) => ({ center, index }))
    .sort((a, b) => a.center - b.center)
  const result = Array(targets.length).fill(0)
  sortedTargetIndices.forEach((target, sortedIndex) => {
    result[target.index] = valid[sortedIndex].view
  })
  return result
}

function finalizeResult(
  state: AppState,
  settings: SimulationSettings,
  accumulator: RayAccumulator,
  model: SimulationResult['model'],
  warnings: string[],
  startTime: number,
  characterization?: SimulationResult['characterization'],
): SimulationResult {
  const centersResult = targetCenters(state)
  if (centersResult.missing.length) throw new Error(`Missing: ${centersResult.missing.join(', ')}`)
  if (!isPositive(state.view.pupilDiameterMm)) throw new Error('Pupil or camera-aperture diameter is required.')
  const intensity = accumulator.bins.map((bins, view) =>
    characterizeSeries(view, [...accumulator.xMm], bins),
  )
  const desiredSourceByTarget = assignSourcesToTargets(intensity, centersResult.centers)
  const transferMatrix = centersResult.centers.map((target) =>
    accumulator.bins.map((bins) =>
      integratePupil(
        bins,
        accumulator.xMm,
        accumulator.binWidth,
        target,
        state.view.pupilDiameterMm!,
      ),
    ),
  )
  const crosstalk: number[] = []
  const desiredTransmission: number[] = []
  const zoneCenterErrorsMm: number[] = []
  transferMatrix.forEach((row, targetIndex) => {
    const desiredSource = desiredSourceByTarget[targetIndex]
    const total = row.reduce((sum, value) => sum + value, 0)
    const desired = row[desiredSource] ?? 0
    crosstalk.push(total > 1e-12 ? (total - desired) / total : Number.NaN)
    const launched = accumulator.launchedByView[desiredSource] || 1
    desiredTransmission.push(desired / launched)
    const zoneCenter = intensity[desiredSource]?.centroidMm ?? intensity[desiredSource]?.peakMm
    zoneCenterErrorsMm.push(
      isFiniteNumber(zoneCenter) ? zoneCenter - centersResult.centers[targetIndex] : Number.NaN,
    )
    if (total <= 1e-12) {
      warnings.push(`Target ${targetIndex + 1} receives negligible modeled light; crosstalk is not meaningful.`)
    }
  })
  const throughputPerView = accumulator.totalAcceptedByView.map(
    (value, view) => value / Math.max(accumulator.launchedByView[view], 1e-15),
  )
  const relativeThroughput =
    throughputPerView.reduce((sum, value) => sum + value, 0) / throughputPerView.length

  return {
    model,
    intensity,
    transferMatrix,
    desiredSourceByTarget,
    crosstalk,
    desiredTransmission,
    targetCentersMm: centersResult.centers,
    zoneCenterErrorsMm,
    relativeThroughput,
    rays: accumulator.rays,
    discarded: accumulator.discarded,
    ...(characterization ? { characterization } : {}),
    warnings: [...new Set(warnings)],
    elapsedMs: Math.round(performance.now() - startTime),
    settings,
  }
}

export function validateNominalPhysicalInputs(state: AppState): NominalPhysicalInputErrors {
  const errors: NominalPhysicalInputErrors = {}
  const requirePositive = (key: string, value: number | null, label: string) => {
    if (!isPositive(value)) errors[key] = `${label} must be finite and greater than 0.`
  }
  const requirePositiveInteger = (key: string, value: number | null, label: string) => {
    if (!isPositive(value) || !Number.isInteger(value)) {
      errors[key] = `${label} must be a positive whole number.`
    }
  }

  requirePositive('lcd.activeWidthMm', state.lcd.activeWidthMm, 'Active display width')
  requirePositive('lcd.activeHeightMm', state.lcd.activeHeightMm, 'Active display height')
  requirePositiveInteger('lcd.resolutionX', state.lcd.resolutionX, 'Horizontal resolution')
  requirePositiveInteger('lcd.resolutionY', state.lcd.resolutionY, 'Vertical resolution')
  if (state.lcd.sourceMode === 'rgb-stripe') {
    requirePositiveInteger(
      'lcd.horizontalSubpixels',
      state.lcd.horizontalSubpixels,
      'Horizontal subpixel count',
    )
  } else if (state.lcd.sourceMode === 'custom') {
    requirePositive('lcd.customSourcePitchMm', state.lcd.customSourcePitchMm, 'Custom source pitch')
  }
  if (state.lcd.apertureMode === 'fill-factor') {
    if (
      !isPositive(state.lcd.sourceFillFactor) ||
      state.lcd.sourceFillFactor > 1
    ) {
      errors['lcd.sourceFillFactor'] = 'Source fill factor must be finite and between 0 and 1.'
    }
  } else {
    requirePositive('lcd.sourceApertureMm', state.lcd.sourceApertureMm, 'Source aperture')
  }

  if (!Number.isInteger(state.view.views) || state.view.views < 2) {
    errors['view.views'] = 'Number of views must be a whole number of at least 2.'
  }
  requirePositive('view.targetDistanceMm', state.view.targetDistanceMm, 'Target viewing distance')
  requirePositive('view.targetSpacingMm', state.view.targetSpacingMm, 'Adjacent zone spacing')
  requirePositive('view.pupilDiameterMm', state.view.pupilDiameterMm, 'Pupil or camera aperture')

  const enabledLayers = state.layers.filter((layer) => layer.enabled)
  if (!enabledLayers.length) {
    errors.layers = 'At least one enabled optical layer is required.'
  }
  enabledLayers.forEach((layer) => {
    requirePositive(
      `layers.${layer.id}.thicknessMm`,
      layer.thicknessMm,
      `${layer.name} thickness`,
    )
    requirePositive(
      `layers.${layer.id}.refractiveIndex`,
      layer.refractiveIndex,
      `${layer.name} refractive index`,
    )
  })

  if (state.lens.mode === 'thin') {
    const lens = state.lens.thin
    if (lens.useDesignTarget) {
      const design = calculateAnalyticalDesign(state)
      if (!isPositive(design.requiredPitchMm)) {
        errors['lens.thin.pitchMm'] = 'Calculated lens pitch requires complete valid setup inputs.'
      }
      if (!isPositive(design.requiredFocalLengthMm)) {
        errors['lens.thin.focalLengthMm'] = 'Calculated focal length requires complete valid setup inputs.'
      }
    } else {
      requirePositive('lens.thin.pitchMm', lens.pitchMm, 'Actual thin-lens pitch')
      requirePositive('lens.thin.focalLengthMm', lens.focalLengthMm, 'Thin-lens focal length')
      if (lens.focalReference === 'vendor-unspecified') {
        errors['lens.thin.focalLengthMm'] = 'Select an unambiguous focal-length reference.'
      }
    }
    if (!isPositive(lens.fillFactor) || lens.fillFactor > 1) {
      errors['lens.thin.fillFactor'] = 'Lens fill factor must be finite and between 0 and 1.'
    }
  } else {
    const lens = state.lens.thick
    requirePositive('lens.thick.pitchMm', lens.pitchMm, 'Thick-lens pitch')
    requirePositive('lens.thick.radiusMm', lens.radiusMm, 'Radius of curvature')
    requirePositive('lens.thick.refractiveIndex', lens.refractiveIndex, 'Lens refractive index')
    requirePositive('lens.thick.centerThicknessMm', lens.centerThicknessMm, 'Center thickness')
    requirePositive('lens.thick.externalIndex', lens.externalIndex, 'External refractive index')
    if (!isPositive(lens.fillFactor) || lens.fillFactor > 1) {
      errors['lens.thick.fillFactor'] = 'Lens fill factor must be finite and between 0 and 1.'
    }
    const geometry = validateThickGeometry(lens.pitchMm, lens.radiusMm, lens.centerThicknessMm)
    if (geometry.errors.length && !errors['lens.thick.radiusMm']) {
      errors['lens.thick.radiusMm'] = geometry.errors.join(' ')
    }
  }

  const source = calculateSourceGeometry(state)
  if (!isPositive(source.sourcePitchMm)) {
    const key = state.lcd.sourceMode === 'custom'
      ? 'lcd.customSourcePitchMm'
      : state.lcd.sourceMode === 'rgb-stripe'
        ? 'lcd.horizontalSubpixels'
        : 'lcd.resolutionX'
    errors[key] ??= 'Source geometry must resolve to a finite positive pitch.'
  }
  if (!isPositive(source.sourceApertureMm)) {
    const key = state.lcd.apertureMode === 'fill-factor'
      ? 'lcd.sourceFillFactor'
      : 'lcd.sourceApertureMm'
    errors[key] ??= 'Source geometry must resolve to a finite positive aperture.'
  }

  return errors
}

function validateCommonSimulationInputs(state: AppState) {
  const inputErrors = validateNominalPhysicalInputs(state)
  if (Object.keys(inputErrors).length) throw new Error(Object.values(inputErrors).join(' '))
  const design = calculateAnalyticalDesign(state)
  const source = calculateSourceGeometry(state)
  if (design.missing.length) throw new Error(`Missing: ${design.missing.join(', ')}`)
  if (!isPositive(source.sourcePitchMm) || !isPositive(source.sourceApertureMm)) {
    throw new Error('Valid source pitch and aperture are required.')
  }
  if (!isPositive(design.gap.reducedGapMm)) throw new Error('Reduced optical gap is required.')
  if (!isPositive(state.view.targetDistanceMm)) throw new Error('Target viewing distance is required.')
  if (!isPositive(state.lcd.activeWidthMm)) throw new Error('Active display width is required.')
  return { design, source }
}

export async function simulateParaxial(
  state: AppState,
  callbacks: SimulationCallbacks = {},
  settingsOverride?: SimulationSettings,
): Promise<SimulationResult> {
  const startTime = performance.now()
  const { design, source } = validateCommonSimulationInputs(state)
  const settings = effectiveSimulationSettings(state, settingsOverride ?? state.simulationSettings)
  const pitch = state.lens.thin.useDesignTarget
    ? design.requiredPitchMm
    : state.lens.thin.pitchMm
  const focal = state.lens.thin.useDesignTarget
    ? design.requiredFocalLengthMm
    : state.lens.thin.focalLengthMm
  if (!isPositive(pitch)) throw new Error('Actual thin-lens pitch is required, or enable design target.')
  if (!isPositive(focal)) throw new Error('Thin-lens focal length is required, or enable design target.')
  if (!state.lens.thin.useDesignTarget && state.lens.thin.focalReference === 'vendor-unspecified') {
    throw new Error('Select an unambiguous focal-length reference convention before simulation.')
  }
  const fillFactor = state.lens.thin.fillFactor
  if (!isPositive(fillFactor) || fillFactor > 1) throw new Error('Lens fill factor must be between 0 and 1.')

  const accumulator = createAccumulator(state, settings)
  const d = design.gap.reducedGapMm!
  const z = state.view.targetDistanceMm!
  const q = source.sourcePitchMm!
  const G = source.repeatWidthMm!
  const sourceMediumIndex = state.layers.find(
    (layer) => layer.enabled && isPositive(layer.refractiveIndex),
  )!.refractiveIndex!
  const sourceFill = clamp(source.sourceApertureMm! / q, 0, 1)
  const repeats = repeatIndices(state.lcd.activeWidthMm!, G, settings.lenslets)
  const sourceOffsets = midpointSamples(source.sourceApertureMm!, settings.sourceSamples)
  const apertureOffsets = midpointSamples(pitch, settings.apertureSamples)
  const warnings = [
    'Relative intensity omits Fresnel reflection, absorption, scattering, polarization, and source radiometric calibration.',
    'Positive source offsets form zones on the opposite side of the optical axis; view assignment is reported explicitly.',
  ]
  const totalOuter = state.view.views * repeats.length
  let outer = 0

  for (let view = 0; view < state.view.views; view += 1) {
    const xViewSource = (view - (state.view.views - 1) / 2) * q
    for (const repeat of repeats) {
      const sourceCenter = repeat * G + xViewSource + state.lens.sourcePhaseMm
      const lensCenter = repeat * pitch + (state.lens.thin.lateralOffsetMm ?? 0)
      for (let sourceIndex = 0; sourceIndex < sourceOffsets.length; sourceIndex += 1) {
        const sourceX = sourceCenter + sourceOffsets[sourceIndex]
        for (let apertureIndex = 0; apertureIndex < apertureOffsets.length; apertureIndex += 1) {
          const apertureOffset = apertureOffsets[apertureIndex]
          const lensX = lensCenter + apertureOffset
          const uIn = (lensX - sourceX) / d
          const angle = paraxialSourceAngle(uIn, sourceMediumIndex)
          const baseWeight =
            (emissionWeight(state, angle) * sourceFill) /
            (repeats.length * sourceOffsets.length * apertureOffsets.length)
          accumulator.launchedByView[view] += baseWeight
          accumulator.discarded.total += 1
          if (Math.abs(apertureOffset) > (pitch * fillFactor) / 2 || baseWeight <= 0) {
            accumulator.discarded.missedSurface += 1
            continue
          }
          const uOut = uIn - apertureOffset / focal
          const viewerX = lensX + z * uOut
          addToBin(accumulator, view, viewerX, baseWeight)

          const recordRay =
            accumulator.rays.length < 180 &&
            (sourceIndex === 0 || sourceIndex === Math.floor(sourceOffsets.length / 2)) &&
            apertureIndex % Math.max(1, Math.floor(apertureOffsets.length / 5)) === 0
          if (recordRay) {
            accumulator.rays.push({
              view,
              weight: baseWeight,
              outcome: 'transmitted',
              points: [
                { x: sourceX, y: -d },
                { x: lensX, y: 0 },
                { x: viewerX, y: z },
              ],
            })
          }
        }
      }
      outer += 1
      if (outer % 2 === 0 || outer === totalOuter) {
        await checkpoint(callbacks, outer / totalOuter, `Tracing source view ${view + 1} of ${state.view.views}`)
      }
    }
  }

  return finalizeResult(
    state,
    settings,
    accumulator,
    MODEL_LABELS.paraxial,
    warnings,
    startTime,
  )
}

function displacementForInvariant(
  invariant: number,
  layers: AppState['layers'],
): number | null {
  let displacement = 0
  for (const layer of layers) {
    if (!layer.enabled || !isPositive(layer.thicknessMm) || !isPositive(layer.refractiveIndex)) continue
    const denominatorSquared = layer.refractiveIndex ** 2 - invariant ** 2
    if (denominatorSquared <= 0) return null
    displacement += (layer.thicknessMm * invariant) / Math.sqrt(denominatorSquared)
  }
  return displacement
}

export function solveTangentialInvariant(
  deltaX: number,
  layers: AppState['layers'],
): number | null {
  const enabled = layers.filter(
    (layer) => layer.enabled && isPositive(layer.thicknessMm) && isPositive(layer.refractiveIndex),
  )
  if (!enabled.length) return null
  if (Math.abs(deltaX) < 1e-14) return 0
  const minIndex = Math.min(...enabled.map((layer) => layer.refractiveIndex!))
  let low = -minIndex * (1 - 1e-12)
  let high = minIndex * (1 - 1e-12)
  const evaluate = (value: number) => {
    const displacement = displacementForInvariant(value, enabled)
    return displacement === null ? null : displacement - deltaX
  }
  let fLow = evaluate(low)
  let fHigh = evaluate(high)
  if (fLow === null || fHigh === null || fLow > 0 || fHigh < 0) return null

  let current = clamp(
    deltaX /
      enabled.reduce((sum, layer) => sum + layer.thicknessMm! / layer.refractiveIndex!, 0),
    low,
    high,
  )
  for (let iteration = 0; iteration < 64; iteration += 1) {
    const fCurrent = evaluate(current)
    if (fCurrent === null) return null
    if (Math.abs(fCurrent) < 1e-11) return current
    if (fCurrent > 0) {
      high = current
      fHigh = fCurrent
    } else {
      low = current
      fLow = fCurrent
    }
    let derivative = 0
    for (const layer of enabled) {
      const base = layer.refractiveIndex! ** 2 - current ** 2
      derivative +=
        (layer.thicknessMm! * layer.refractiveIndex! ** 2) / Math.max(base, 1e-20) ** 1.5
    }
    const newton = current - fCurrent / derivative
    current = newton > low && newton < high ? newton : (low + high) / 2
  }
  const finalValue = (low + high) / 2
  return isFiniteNumber(evaluate(finalValue)) ? finalValue : null
}

function traceFlatStack(
  sourceX: number,
  rearX: number,
  layers: AppState['layers'],
): { invariant: number; points: Vec2[]; sourceAngle: number } | null {
  const enabled = layers.filter(
    (layer) => layer.enabled && isPositive(layer.thicknessMm) && isPositive(layer.refractiveIndex),
  )
  const invariant = solveTangentialInvariant(rearX - sourceX, enabled)
  if (!isFiniteNumber(invariant)) return null
  const totalThickness = enabled.reduce((sum, layer) => sum + layer.thicknessMm!, 0)
  const points: Vec2[] = [{ x: sourceX, y: -totalThickness }]
  let x = sourceX
  let y = -totalThickness
  for (const layer of enabled) {
    const denominator = Math.sqrt(layer.refractiveIndex! ** 2 - invariant ** 2)
    x += (layer.thicknessMm! * invariant) / denominator
    y += layer.thicknessMm!
    points.push({ x, y })
  }
  points[points.length - 1] = { x: rearX, y: 0 }
  return {
    invariant,
    points,
    sourceAngle: Math.asin(clamp(invariant / enabled[0].refractiveIndex!, -1, 1)),
  }
}

function normalize(vector: Vec2): Vec2 {
  const magnitude = Math.hypot(vector.x, vector.y)
  return magnitude > 1e-15
    ? { x: vector.x / magnitude, y: vector.y / magnitude }
    : { x: 0, y: 0 }
}

function intersectCircularSurface(
  origin: Vec2,
  direction: Vec2,
  lensCenterX: number,
  radius: number,
  centerThickness: number,
  pitch: number,
): { point: Vec2; normal: Vec2; distance: number } | null {
  const circleCenter = { x: lensCenterX, y: centerThickness - radius }
  const offset = { x: origin.x - circleCenter.x, y: origin.y - circleCenter.y }
  const b = 2 * (offset.x * direction.x + offset.y * direction.y)
  const c = offset.x ** 2 + offset.y ** 2 - radius ** 2
  const discriminant = b ** 2 - 4 * c
  if (discriminant < 0) return null
  const root = Math.sqrt(Math.max(0, discriminant))
  const candidates = [(-b - root) / 2, (-b + root) / 2]
    .filter((distance) => distance > 1e-10)
    .sort((a, bValue) => a - bValue)
  for (const distance of candidates) {
    const point = {
      x: origin.x + direction.x * distance,
      y: origin.y + direction.y * distance,
    }
    const localX = point.x - lensCenterX
    if (Math.abs(localX) > pitch / 2 + 1e-9) continue
    if (point.y < circleCenter.y - 1e-9) continue
    return {
      point,
      normal: normalize({ x: point.x - circleCenter.x, y: point.y - circleCenter.y }),
      distance,
    }
  }
  return null
}

export function intersectPeriodicCircularSurface(
  origin: Vec2,
  direction: Vec2,
  nominalLensCenterX: number,
  radius: number,
  centerThickness: number,
  pitch: number,
): { point: Vec2; normal: Vec2; lensCenterX: number } | null {
  const rayDirection = normalize(direction)
  if (!isPositive(pitch) || rayDirection.y <= 0 || centerThickness <= origin.y) return null

  const travelToVertexPlane = (centerThickness - origin.y) / rayDirection.y
  const endX = origin.x + rayDirection.x * travelToVertexPlane
  const minimumOffset = Math.floor(
    (Math.min(origin.x, endX) - nominalLensCenterX) / pitch,
  ) - 1
  const maximumOffset = Math.ceil(
    (Math.max(origin.x, endX) - nominalLensCenterX) / pitch,
  ) + 1

  let nearest: {
    point: Vec2
    normal: Vec2
    lensCenterX: number
    distance: number
  } | null = null
  for (let offset = minimumOffset; offset <= maximumOffset; offset += 1) {
    const lensCenterX = nominalLensCenterX + offset * pitch
    const hit = intersectCircularSurface(
      origin,
      rayDirection,
      lensCenterX,
      radius,
      centerThickness,
      pitch,
    )
    if (hit && (!nearest || hit.distance < nearest.distance)) {
      nearest = { ...hit, lensCenterX }
    }
  }
  return nearest
}

function traceThickSingle(
  state: AppState,
  sourceX: number,
  rearX: number,
  lensCenterX: number,
  refractiveIndexOverride?: number,
): {
  outcome: RayPath['outcome']
  points: Vec2[]
  viewerX: number | null
  exitAngle: number | null
} {
  const lens = state.lens.thick
  const pitch = lens.pitchMm!
  const radius = lens.radiusMm!
  const thickness = lens.centerThicknessMm!
  const lensIndex = refractiveIndexOverride ?? lens.refractiveIndex!
  const externalIndex = lens.externalIndex!
  const flat = traceFlatStack(sourceX, rearX, state.layers)
  if (!flat || Math.abs(flat.invariant) >= lensIndex) {
    return { outcome: 'unreachable', points: flat?.points ?? [], viewerX: null, exitAngle: null }
  }
  const lensDirection = normalize({
    x: flat.invariant / lensIndex,
    y: Math.sqrt(Math.max(0, 1 - (flat.invariant / lensIndex) ** 2)),
  })
  const hit = intersectPeriodicCircularSurface(
    { x: rearX, y: 0 },
    lensDirection,
    lensCenterX,
    radius,
    thickness,
    pitch,
  )
  if (!hit) return { outcome: 'missed', points: flat.points, viewerX: null, exitAngle: null }
  const points = [...flat.points, hit.point]
  if (Math.abs(hit.point.x - hit.lensCenterX) > (pitch * lens.fillFactor!) / 2) {
    return { outcome: 'missed', points, viewerX: null, exitAngle: null }
  }
  const outgoing = refractVector(lensDirection, hit.normal, lensIndex, externalIndex)
  if (!outgoing) return { outcome: 'tir', points, viewerX: null, exitAngle: null }
  const viewPlaneY = thickness + state.view.targetDistanceMm!
  if (outgoing.y <= 0 || viewPlaneY <= hit.point.y) {
    return { outcome: 'missed', points, viewerX: null, exitAngle: null }
  }
  const distance = (viewPlaneY - hit.point.y) / outgoing.y
  const viewer = { x: hit.point.x + outgoing.x * distance, y: viewPlaneY }
  return {
    outcome: 'transmitted',
    points: [...points, viewer],
    viewerX: viewer.x,
    exitAngle: Math.atan2(outgoing.x, outgoing.y),
  }
}

function predictThickConjugateDistance(state: AppState): number | null {
  if (!isPositive(state.lens.thick.pitchMm) || !isPositive(state.lens.thick.centerThicknessMm)) {
    return null
  }
  const distances: number[] = []
  for (const fraction of [-0.02, -0.01, 0.01, 0.02]) {
    const rearX = state.lens.thick.pitchMm * fraction
    const traced = traceThickSingle(state, 0, rearX, 0)
    if (
      traced.outcome !== 'transmitted' ||
      !isFiniteNumber(traced.exitAngle) ||
      Math.abs(Math.tan(traced.exitAngle)) < 1e-12 ||
      traced.points.length < 2
    ) {
      continue
    }
    const surface = traced.points[traced.points.length - 2]
    const focusY = surface.y - surface.x / Math.tan(traced.exitAngle)
    const distanceFromFrontVertex = focusY - state.lens.thick.centerThicknessMm
    if (distanceFromFrontVertex > 0 && Number.isFinite(distanceFromFrontVertex)) {
      distances.push(distanceFromFrontVertex)
    }
  }
  return distances.length
    ? distances.reduce((sum, value) => sum + value, 0) / distances.length
    : null
}

function weightedStandardDeviation(values: Array<{ angle: number; weight: number }>): number | null {
  const total = values.reduce((sum, item) => sum + item.weight, 0)
  if (total <= 1e-15) return null
  const mean = values.reduce((sum, item) => sum + item.angle * item.weight, 0) / total
  const variance =
    values.reduce((sum, item) => sum + (item.angle - mean) ** 2 * item.weight, 0) / total
  return Math.sqrt(Math.max(0, variance))
}

function chromaticCentroid(
  state: AppState,
  index: number,
  repeats: number[],
  sourceOffsets: number[],
  apertureOffsets: number[],
  sourcePitch: number,
  repeatWidth: number,
): number | null {
  const view = Math.floor((state.view.views - 1) / 2)
  const xViewSource = (view - (state.view.views - 1) / 2) * sourcePitch
  let sum = 0
  let weightSum = 0
  for (const repeat of repeats.slice(0, Math.min(repeats.length, 9))) {
    const sourceCenter = repeat * repeatWidth + xViewSource + state.lens.sourcePhaseMm
    const lensCenter = repeat * state.lens.thick.pitchMm! + (state.lens.thick.lateralOffsetMm ?? 0)
    for (const sourceOffset of sourceOffsets.slice(0, Math.min(sourceOffsets.length, 5))) {
      for (const apertureOffset of apertureOffsets.slice(0, Math.min(apertureOffsets.length, 15))) {
        const trace = traceThickSingle(
          state,
          sourceCenter + sourceOffset,
          lensCenter + apertureOffset,
          lensCenter,
          index,
        )
        if (trace.outcome === 'transmitted' && isFiniteNumber(trace.viewerX)) {
          sum += trace.viewerX
          weightSum += 1
        }
      }
    }
  }
  return weightSum ? sum / weightSum : null
}

export async function simulateThick(
  state: AppState,
  callbacks: SimulationCallbacks = {},
  settingsOverride?: SimulationSettings,
): Promise<SimulationResult> {
  const startTime = performance.now()
  const { source } = validateCommonSimulationInputs(state)
  const settings = effectiveSimulationSettings(state, settingsOverride ?? state.simulationSettings)
  const lens = state.lens.thick
  const geometry = validateThickGeometry(lens.pitchMm, lens.radiusMm, lens.centerThicknessMm)
  if (!geometry.valid) throw new Error(`Invalid thick-lens geometry: ${geometry.errors.join(', ')}`)
  if (!isPositive(lens.refractiveIndex)) throw new Error('Lens refractive index is required.')
  if (!isPositive(lens.externalIndex)) throw new Error('External refractive index is required.')
  if (!isPositive(lens.fillFactor) || lens.fillFactor > 1) {
    throw new Error('Lens fill factor must be between 0 and 1.')
  }
  const pitch = lens.pitchMm!
  const q = source.sourcePitchMm!
  const G = source.repeatWidthMm!
  const sourceFill = clamp(source.sourceApertureMm! / q, 0, 1)
  const repeats = repeatIndices(state.lcd.activeWidthMm!, G, settings.lenslets)
  const sourceOffsets = midpointSamples(source.sourceApertureMm!, settings.sourceSamples)
  const apertureOffsets = midpointSamples(pitch, settings.apertureSamples)
  const accumulator = createAccumulator(state, settings)
  const warnings = [
    'Viewing distance in thick-lens mode is measured from the viewer-facing front vertex.',
    'Relative intensity omits Fresnel reflection, absorption, scattering, polarization, and source radiometric calibration.',
  ]
  const totalOuter = state.view.views * repeats.length
  let outer = 0

  for (let view = 0; view < state.view.views; view += 1) {
    const xViewSource = (view - (state.view.views - 1) / 2) * q
    for (const repeat of repeats) {
      const sourceCenter = repeat * G + xViewSource + state.lens.sourcePhaseMm
      const lensCenter = repeat * pitch + (lens.lateralOffsetMm ?? 0)
      for (let sourceIndex = 0; sourceIndex < sourceOffsets.length; sourceIndex += 1) {
        const sourceX = sourceCenter + sourceOffsets[sourceIndex]
        for (let apertureIndex = 0; apertureIndex < apertureOffsets.length; apertureIndex += 1) {
          const rearX = lensCenter + apertureOffsets[apertureIndex]
          const flat = traceFlatStack(sourceX, rearX, state.layers)
          const sourceAngle = flat?.sourceAngle ?? Math.PI / 2
          const baseWeight =
            (emissionWeight(state, sourceAngle) * sourceFill) /
            (repeats.length * sourceOffsets.length * apertureOffsets.length)
          accumulator.launchedByView[view] += baseWeight
          accumulator.discarded.total += 1
          if (baseWeight <= 0) continue
          const traced = traceThickSingle(state, sourceX, rearX, lensCenter)
          if (traced.outcome === 'unreachable') accumulator.discarded.unreachable += 1
          else if (traced.outcome === 'tir') accumulator.discarded.tir += 1
          else if (traced.outcome === 'missed') accumulator.discarded.missedSurface += 1
          else if (isFiniteNumber(traced.viewerX) && isFiniteNumber(traced.exitAngle)) {
            addToBin(accumulator, view, traced.viewerX, baseWeight)
            accumulator.exitAngles.push({ angle: traced.exitAngle, weight: baseWeight })
          }

          const recordRay =
            accumulator.rays.length < 180 &&
            (sourceIndex === 0 || sourceIndex === Math.floor(sourceOffsets.length / 2)) &&
            apertureIndex % Math.max(1, Math.floor(apertureOffsets.length / 5)) === 0
          if (recordRay) {
            accumulator.rays.push({
              view,
              weight: baseWeight,
              outcome: traced.outcome,
              points: traced.points,
            })
          }
        }
      }
      outer += 1
      if (outer % 2 === 0 || outer === totalOuter) {
        await checkpoint(callbacks, outer / totalOuter, `Tracing source view ${view + 1} of ${state.view.views}`)
      }
    }
  }

  const characterization = estimateThickParaxial(
    pitch,
    lens.radiusMm,
    lens.refractiveIndex,
    lens.centerThicknessMm,
    lens.externalIndex,
  )
  const angularSpread = weightedStandardDeviation(accumulator.exitAngles)
  characterization.outputAngularSpreadDeg = angularSpread ? (angularSpread * 180) / Math.PI : null
  characterization.relativeThroughput =
    accumulator.totalAcceptedByView.reduce((sum, value) => sum + value, 0) /
    Math.max(accumulator.launchedByView.reduce((sum, value) => sum + value, 0), 1e-15)
  characterization.tirFraction = accumulator.discarded.tir / Math.max(accumulator.discarded.total, 1)

  const chromaticEntries = Object.entries(state.lcd.rgb).filter(
    ([, value]) => isPositive(value.wavelengthNm) && isPositive(value.refractiveIndex),
  )
  if (chromaticEntries.length >= 2) {
    const centroids: Record<string, number | null> = {}
    for (const [name, value] of chromaticEntries) {
      centroids[`${name} ${value.wavelengthNm} nm`] = chromaticCentroid(
        state,
        value.refractiveIndex!,
        repeats,
        sourceOffsets,
        apertureOffsets,
        q,
        G,
      )
    }
    const reference = centroids[Object.keys(centroids).find((key) => key.startsWith('green')) ?? '']
    characterization.chromaticShiftMm = Object.fromEntries(
      Object.entries(centroids).map(([key, value]) => [
        key,
        isFiniteNumber(value) && isFiniteNumber(reference) ? value - reference : null,
      ]),
    )
  } else {
    warnings.push('Chromatic shift requires at least two explicit wavelength/index pairs.')
  }

  return finalizeResult(
    state,
    settings,
    accumulator,
    MODEL_LABELS.geometric,
    warnings,
    startTime,
    characterization,
  )
}

export function mulberry32(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state += 0x6d2b79f5
    let value = state
    value = Math.imul(value ^ (value >>> 15), value | 1)
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61)
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296
  }
}

function normalSample(random: () => number): number {
  const u1 = Math.max(random(), 1e-15)
  const u2 = random()
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
}

function drawVariation(
  random: () => number,
  amount: number,
  distribution: DistributionKind,
): number {
  return distribution === 'normal' ? normalSample(random) * amount : (random() * 2 - 1) * amount
}

function perturbPositive(
  nominal: number | null,
  amount: number | null,
  distribution: DistributionKind,
  random: () => number,
  minimum = 0,
): number | null {
  if (!isPositive(nominal)) return null
  if (!isPositive(amount)) return nominal
  for (let attempt = 0; attempt < 32; attempt += 1) {
    const candidate = nominal + drawVariation(random, amount, distribution)
    if (candidate > minimum && Number.isFinite(candidate)) return candidate
  }
  return null
}

function percentile(values: number[], probability: number): number | null {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b)
  if (!sorted.length) return null
  const position = (sorted.length - 1) * probability
  const lower = Math.floor(position)
  const upper = Math.ceil(position)
  if (lower === upper) return sorted[lower]
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower)
}

function summarizePercentiles(values: number[]): Percentiles {
  return {
    p05: percentile(values, 0.05),
    p50: percentile(values, 0.5),
    p95: percentile(values, 0.95),
  }
}

function ranks(values: number[]): number[] {
  const sorted = values.map((value, index) => ({ value, index })).sort((a, b) => a.value - b.value)
  const result = Array(values.length).fill(0)
  let cursor = 0
  while (cursor < sorted.length) {
    let end = cursor
    while (end + 1 < sorted.length && sorted[end + 1].value === sorted[cursor].value) end += 1
    const averageRank = (cursor + end) / 2 + 1
    for (let index = cursor; index <= end; index += 1) result[sorted[index].index] = averageRank
    cursor = end + 1
  }
  return result
}

function pearson(left: number[], right: number[]): number {
  if (left.length !== right.length || left.length < 3) return 0
  const meanLeft = left.reduce((sum, value) => sum + value, 0) / left.length
  const meanRight = right.reduce((sum, value) => sum + value, 0) / right.length
  let numerator = 0
  let denominatorLeft = 0
  let denominatorRight = 0
  left.forEach((value, index) => {
    const a = value - meanLeft
    const b = right[index] - meanRight
    numerator += a * b
    denominatorLeft += a * a
    denominatorRight += b * b
  })
  const denominator = Math.sqrt(denominatorLeft * denominatorRight)
  return denominator > 1e-15 ? numerator / denominator : 0
}

function spearman(left: number[], right: number[]): number {
  return pearson(ranks(left), ranks(right))
}

export interface SampledTrialState {
  state: AppState
  parameters: Record<string, number>
  valid: boolean
}

export function sampleTrialState(
  nominal: AppState,
  settings: MonteCarloSettings,
  random: () => number,
): SampledTrialState {
  const nominalDesign = calculateAnalyticalDesign(nominal)
  const state = structuredClone(nominal)
  const parameters: Record<string, number> = {}
  const invalid = (): SampledTrialState => ({ state, parameters, valid: false })
  for (const layer of state.layers) {
    if (!layer.enabled) continue
    const thickness = perturbPositive(
      layer.thicknessMm,
      layer.thicknessToleranceMm,
      layer.distribution,
      random,
    )
    const index = perturbPositive(
      layer.refractiveIndex,
      layer.indexTolerance,
      layer.distribution,
      random,
      0.999,
    )
    if (!thickness || !index) return invalid()
    layer.thicknessMm = thickness
    layer.refractiveIndex = index
    parameters[`${layer.name} thickness`] = thickness
    parameters[`${layer.name} index`] = index
  }

  if (state.lens.mode === 'thin') {
    const nominalPitch = state.lens.thin.useDesignTarget
      ? nominalDesign.requiredPitchMm
      : state.lens.thin.pitchMm
    const nominalFocal = state.lens.thin.useDesignTarget
      ? nominalDesign.requiredFocalLengthMm
      : state.lens.thin.focalLengthMm
    const pitch = perturbPositive(
      nominalPitch,
      state.lens.thin.pitchToleranceMm,
      settings.defaultDistribution,
      random,
    )
    const focal = perturbPositive(
      nominalFocal,
      state.lens.thin.focalToleranceMm,
      settings.defaultDistribution,
      random,
    )
    if (!pitch || !focal) return invalid()
    state.lens.thin.useDesignTarget = false
    state.lens.thin.pitchMm = pitch
    state.lens.thin.lpi = 25.4 / pitch
    state.lens.thin.focalLengthMm = focal
    state.lens.thin.focalReference = 'effective-at-plane'
    state.lens.thin.lateralOffsetMm =
      (state.lens.thin.lateralOffsetMm ?? 0) +
      drawVariation(
        random,
        state.lens.thin.offsetToleranceMm ?? 0,
        settings.defaultDistribution,
      )
    parameters['lens pitch'] = pitch
    parameters['focal length'] = focal
    parameters['lateral phase'] = state.lens.thin.lateralOffsetMm
  } else {
    const thick = state.lens.thick
    const pitch = perturbPositive(
      thick.pitchMm,
      thick.pitchToleranceMm,
      settings.defaultDistribution,
      random,
    )
    const radius = perturbPositive(
      thick.radiusMm,
      thick.radiusToleranceMm,
      settings.defaultDistribution,
      random,
    )
    const index = perturbPositive(
      thick.refractiveIndex,
      thick.indexTolerance,
      settings.defaultDistribution,
      random,
      0.999,
    )
    const thickness = perturbPositive(
      thick.centerThicknessMm,
      thick.thicknessToleranceMm,
      settings.defaultDistribution,
      random,
    )
    if (!pitch || !radius || !index || !thickness) return invalid()
    thick.pitchMm = pitch
    thick.radiusMm = radius
    thick.refractiveIndex = index
    thick.centerThicknessMm = thickness
    const offsetTolerance = thick.offsetToleranceMm ?? 0
    thick.lateralOffsetMm =
      (thick.lateralOffsetMm ?? 0) +
      drawVariation(random, offsetTolerance, settings.defaultDistribution)
    parameters['lens pitch'] = pitch
    parameters['radius'] = radius
    parameters['lens index'] = index
    parameters['center thickness'] = thickness
    parameters['lateral phase'] = thick.lateralOffsetMm
    if (!validateThickGeometry(pitch, radius, thickness).valid) return invalid()
  }

  if (isPositive(state.view.targetDistanceMm) && settings.targetDistanceToleranceMm > 0) {
    const distance = perturbPositive(
      state.view.targetDistanceMm,
      settings.targetDistanceToleranceMm,
      settings.defaultDistribution,
      random,
    )
    if (!distance) return invalid()
    state.view.targetDistanceMm = distance
    parameters['target viewing distance'] = distance
  }
  const source = calculateSourceGeometry(state)
  if (isPositive(source.sourceApertureMm) && settings.sourceApertureToleranceMm > 0) {
    const aperture = perturbPositive(
      source.sourceApertureMm,
      settings.sourceApertureToleranceMm,
      settings.defaultDistribution,
      random,
    )
    if (!aperture) return invalid()
    state.lcd.apertureMode = 'width'
    state.lcd.sourceApertureMm = aperture
    parameters['source aperture'] = aperture
  }
  if (settings.pupilPositionToleranceMm > 0) {
    const offset = drawVariation(
      random,
      settings.pupilPositionToleranceMm,
      settings.defaultDistribution,
    )
    const centers = targetCenters(state)
    if (centers.missing.length) return invalid()
    state.view.centerMode = 'custom'
    state.view.customCentersMm = centers.centers.map((center) => center + offset)
    parameters['pupil position'] = offset
  }
  return { state, parameters, valid: true }
}

export function maximumCriterionSeverity(metric: number, maximum: number): number {
  if (!Number.isFinite(metric) || metric < 0 || !Number.isFinite(maximum) || maximum < 0) {
    return Number.POSITIVE_INFINITY
  }
  if (maximum === 0) return metric === 0 ? 0 : Number.POSITIVE_INFINITY
  return metric / maximum
}

function criterionSeverity(
  state: AppState,
  metrics: {
    crosstalk: number
    zoneError: number
    throughput: number
    drift: number
    predictedDistance: number | null
  },
): { passes: boolean; severity: number } {
  const checks: number[] = []
  const enabled = state.toleranceSettings.includeCriteria
  const criteria = state.view.criteria
  if (enabled.crosstalk) {
    checks.push(maximumCriterionSeverity(metrics.crosstalk, criteria.maxCrosstalk))
  }
  if (enabled.zoneCenter) {
    checks.push(maximumCriterionSeverity(metrics.zoneError, criteria.maxZoneCenterErrorMm))
  }
  if (enabled.throughput) checks.push(criteria.minRelativeThroughput / Math.max(metrics.throughput, 1e-15))
  if (enabled.phaseDrift) {
    checks.push(
      maximumCriterionSeverity(
        Math.abs(metrics.drift),
        criteria.maxAccumulatedDriftSourceCells,
      ),
    )
  }
  if (enabled.viewingRange) {
    if (
      !isFiniteNumber(metrics.predictedDistance) ||
      !isPositive(state.view.distanceMinMm) ||
      !isPositive(state.view.distanceMaxMm)
    ) {
      checks.push(Number.POSITIVE_INFINITY)
    } else if (
      metrics.predictedDistance < state.view.distanceMinMm ||
      metrics.predictedDistance > state.view.distanceMaxMm
    ) {
      const center = (state.view.distanceMinMm + state.view.distanceMaxMm) / 2
      const halfRange = Math.max((state.view.distanceMaxMm - state.view.distanceMinMm) / 2, 1e-9)
      checks.push(1 + Math.abs(metrics.predictedDistance - center) / halfRange)
    } else {
      checks.push(1)
    }
  }
  const severity = checks.length ? Math.max(...checks) : 0
  return { passes: Number.isFinite(severity) && severity <= 1, severity }
}

function validateMonteCarloInputs(state: AppState): void {
  const enabled = state.toleranceSettings.includeCriteria
  const criteria = state.view.criteria
  const requireNonNegative = (included: boolean, value: number, label: string) => {
    if (included && (!Number.isFinite(value) || value < 0)) {
      throw new Error(`${label} must be finite and at least 0 when included.`)
    }
  }

  requireNonNegative(enabled.crosstalk, criteria.maxCrosstalk, 'Maximum crosstalk')
  requireNonNegative(enabled.zoneCenter, criteria.maxZoneCenterErrorMm, 'Maximum zone-center error')
  requireNonNegative(
    enabled.phaseDrift,
    criteria.maxAccumulatedDriftSourceCells,
    'Maximum accumulated drift',
  )
  if (
    enabled.throughput &&
    (!Number.isFinite(criteria.minRelativeThroughput) || criteria.minRelativeThroughput < 0)
  ) {
    throw new Error('Minimum relative throughput must be finite and at least 0 when included.')
  }
  if (enabled.viewingRange) {
    if (!isPositive(state.view.distanceMinMm) || !isPositive(state.view.distanceMaxMm)) {
      throw new Error('Viewing-distance range bounds must be finite and greater than 0 when included.')
    }
    if (state.view.distanceMinMm > state.view.distanceMaxMm) {
      throw new Error('Viewing-distance range minimum must be less than or equal to maximum.')
    }
  }
}

export async function runMonteCarlo(
  state: AppState,
  callbacks: SimulationCallbacks = {},
): Promise<MonteCarloResult> {
  const settings = state.toleranceSettings
  if (settings.mode === 'analytical-paraxial' && state.lens.mode !== 'thin') {
    throw new Error('Analytical/paraxial tolerance mode requires the active thin-lens model.')
  }
  if (settings.mode === 'geometric-ray' && state.lens.mode !== 'thick') {
    throw new Error('Geometric-ray tolerance mode requires the active thick-lens model.')
  }
  const inputErrors = validateNominalPhysicalInputs(state)
  if (Object.keys(inputErrors).length) throw new Error(Object.values(inputErrors).join(' '))
  validateMonteCarloInputs(state)
  const startTime = performance.now()
  const trials = clamp(Math.round(settings.trials), 10, settings.mode === 'geometric-ray' ? 2000 : 10000)
  const random = mulberry32(Math.round(settings.seed))
  const distributions = {
    crosstalk: [] as number[],
    zoneCenterErrorMm: [] as number[],
    predictedViewingDistanceMm: [] as number[],
    driftSourceCells: [] as number[],
  }
  const parameterSamples: Record<string, number[]> = {}
  const severities: number[] = []
  const recordFailureAssociation = (
    parameters: Record<string, number>,
    severity: number,
  ) => {
    severities.push(severity)
    Object.entries(parameters).forEach(([name, value]) => {
      ;(parameterSamples[name] ??= []).push(value)
    })
  }
  let successCount = 0
  let invalidTrials = 0
  const smallSettings: SimulationSettings = {
    preset: 'preview',
    sourceSamples: 3,
    apertureSamples: 9,
    lenslets: 5,
    viewerBins: 120,
    viewerSpanMm: Math.max(220, state.simulationSettings.viewerSpanMm),
  }

  for (let trial = 0; trial < trials; trial += 1) {
    if (callbacks.shouldCancel?.()) throw new SimulationCancelledError()
    const sampled = sampleTrialState(state, settings, random)
    if (!sampled.valid) {
      invalidTrials += 1
      recordFailureAssociation(sampled.parameters, 1e6)
    } else {
      try {
        const result =
          settings.mode === 'geometric-ray'
            ? await simulateThick(sampled.state, { shouldCancel: callbacks.shouldCancel }, smallSettings)
            : await simulateParaxial(sampled.state, { shouldCancel: callbacks.shouldCancel }, smallSettings)
        const design = calculateAnalyticalDesign(sampled.state)
        const pitch =
          sampled.state.lens.mode === 'thin'
            ? sampled.state.lens.thin.pitchMm
            : sampled.state.lens.thick.pitchMm
        const mismatch = calculatePitchMismatch(
          pitch,
          design.requiredPitchMm,
          sampled.state.lcd.activeWidthMm,
          design.source.sourcePitchMm,
        )
        let predictedDistance: number | null = null
        if (sampled.state.lens.mode === 'thin') {
          const prediction = predictedImageDistance(
            sampled.state.lens.thin.focalLengthMm,
            design.gap.reducedGapMm,
          )
          predictedDistance = prediction.state === 'real' ? prediction.distanceMm : null
        } else {
          predictedDistance = predictThickConjugateDistance(sampled.state)
        }
        const finiteCrosstalk = result.crosstalk.filter(Number.isFinite)
        const finiteZoneErrors = result.zoneCenterErrorsMm.filter(Number.isFinite).map(Math.abs)
        const finiteThroughput = result.desiredTransmission.filter(Number.isFinite)
        const maxCrosstalk = finiteCrosstalk.length ? Math.max(...finiteCrosstalk) : 1
        const maxZoneError = finiteZoneErrors.length
          ? Math.max(...finiteZoneErrors)
          : Number.POSITIVE_INFINITY
        const minThroughput = finiteThroughput.length ? Math.min(...finiteThroughput) : 0
        const drift = mismatch?.driftSourceCells ?? Number.POSITIVE_INFINITY
        const evaluation = criterionSeverity(sampled.state, {
          crosstalk: maxCrosstalk,
          zoneError: maxZoneError,
          throughput: minThroughput,
          drift,
          predictedDistance,
        })
        if (evaluation.passes) successCount += 1
        distributions.crosstalk.push(maxCrosstalk)
        distributions.zoneCenterErrorMm.push(maxZoneError)
        if (isFiniteNumber(predictedDistance)) {
          distributions.predictedViewingDistanceMm.push(predictedDistance)
        }
        distributions.driftSourceCells.push(drift)
        recordFailureAssociation(
          sampled.parameters,
          Number.isFinite(evaluation.severity) ? evaluation.severity : 1e6,
        )
      } catch (error) {
        if (error instanceof SimulationCancelledError) throw error
        invalidTrials += 1
        recordFailureAssociation(sampled.parameters, 1e6)
      }
    }
    if (trial % Math.max(1, Math.floor(trials / 100)) === 0 || trial === trials - 1) {
      callbacks.onProgress?.((trial + 1) / trials, `Evaluating trial ${trial + 1} of ${trials}`)
      await nextFrame()
    }
  }

  const validTrials = trials - invalidTrials
  const parameterRanking = Object.entries(parameterSamples)
    .filter(([, values]) => values.length === severities.length && values.length >= 3)
    .map(([parameter, values]) => {
      const rho = spearman(values, severities)
      return {
        parameter,
        spearmanRho: rho,
        association:
          Math.abs(rho) < 0.05
            ? 'negligible monotonic association'
            : rho > 0
              ? 'higher values associated with failure severity'
              : 'lower values associated with failure severity',
      }
    })
    .sort((a, b) => Math.abs(b.spearmanRho) - Math.abs(a.spearmanRho))
    .slice(0, 10)

  return {
    model: MODEL_LABELS.monteCarlo,
    evaluationModel:
      settings.mode === 'geometric-ray' ? 'geometric ray' : 'analytical/paraxial',
    trials,
    validTrials,
    invalidTrials,
    seed: settings.seed,
    successRate: trials > 0 ? successCount / trials : 0,
    assumptions: [
      `${settings.defaultDistribution} distributions use entered values as ${settings.defaultDistribution === 'normal' ? 'one standard deviation' : 'half-range'}.`,
      'Parameters are sampled independently; correlations are not modeled.',
      'Invalid physical draws are resampled up to 32 times, then counted as failed trials.',
      'Success means every enabled displayed criterion passed for that trial.',
      'Parameter ranking uses Spearman rank correlation with normalized failure severity.',
    ],
    percentiles: {
      crosstalk: summarizePercentiles(distributions.crosstalk),
      zoneCenterErrorMm: summarizePercentiles(distributions.zoneCenterErrorMm),
      predictedViewingDistanceMm: summarizePercentiles(
        distributions.predictedViewingDistanceMm,
      ),
      driftSourceCells: summarizePercentiles(distributions.driftSourceCells),
    },
    distributions,
    parameterRanking,
    elapsedMs: Math.round(performance.now() - startTime),
  }
}
